import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete, Button, Chip, Grid, MenuItem, TextField } from '@mui/material'
import { Popconfirm } from 'antd'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { trackPromise } from 'react-promise-tracker'
import { PageTitle } from '../../components/PageElements'
import { useNotification } from '../../hooks/useNotification.js'
import services from '../../services/index.js'
import { atualizarMarcas, Marca } from '../../novo/estado'
import { contraste, FONTES, logoDaMarca, textoSobre } from '../../novo/tema'
import './style.scss'

interface Ambiente { id: number; name: string; brandId?: number | null }

const TAMANHO_MAXIMO_LOGO = 300_000
const HEX = /^#[0-9a-fA-F]{6}$/
const NOVA: Marca = { id: 0, nome: '', corPrincipal: '#0a6bff', corApoio: '#c400e0', fonte: 'Arimo', logoClaro: null, logoEscuro: null, ambientes: [] }

function CampoCor({ rotulo, valor, aoMudar, dica }: { rotulo: string; valor: string; aoMudar: (v: string) => void; dica: string }) {
  const valido = HEX.test(valor)
  return (
    <div className="marca-cor">
      <input type="color" value={valido ? valor : '#000000'} onChange={e => aoMudar(e.target.value)} aria-label={`${rotulo}: escolher`} />
      <TextField size="small" label={rotulo} value={valor} onChange={e => aoMudar(e.target.value.trim())}
                 error={!valido} helperText={valido ? dica : 'Use o formato #RRGGBB'} inputProps={{ maxLength: 7 }} fullWidth />
    </div>
  )
}

function CampoLogo({ rotulo, valor, fundo, aoMudar }: { rotulo: string; valor?: string | null; fundo: string; aoMudar: (v: string | null) => void }) {
  const { notify } = useNotification()
  const arquivo = useRef<HTMLInputElement>(null)
  const ler = (f?: File) => {
    if (!f) return
    if (!/^image\/(png|jpeg|webp|svg\+xml)$/.test(f.type)) { notify('Use uma imagem PNG, JPG, WEBP ou SVG.', { variant: 'warning' }); return }
    const leitor = new FileReader()
    leitor.onload = () => {
      const dado = String(leitor.result)
      if (dado.length > TAMANHO_MAXIMO_LOGO) { notify('O logo precisa ter até 300 KB. Prefira SVG ou um PNG menor.', { variant: 'warning' }); return }
      aoMudar(dado)
    }
    leitor.readAsDataURL(f)
  }
  return (
    <div className="marca-logo">
      <span className="marca-rotulo">{rotulo}</span>
      <div className="marca-logo-caixa" style={{ background: fundo }}>
        {valor ? <img src={valor} alt={rotulo} /> : <span>Sem logo próprio</span>}
      </div>
      <div className="marca-logo-acoes">
        <Button size="small" variant="outlined" onClick={() => arquivo.current?.click()}>Escolher imagem</Button>
        {valor && <Button size="small" onClick={() => aoMudar(null)}>Tirar</Button>}
      </div>
      <input ref={arquivo} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden
             onChange={e => { ler(e.target.files?.[0]); e.target.value = '' }} />
    </div>
  )
}

/** A casca do portal novo, em miniatura, com as cores do rascunho. */
function Previa({ m }: { m: Marca }) {
  const valida = HEX.test(m.corPrincipal) && HEX.test(m.corApoio)
  if (!valida) return <div className="marca-previa vazia">A prévia aparece quando as duas cores estiverem no formato #RRGGBB.</div>
  const sobre = textoSobre(m.corPrincipal)
  const ct = contraste(m.corPrincipal, sobre)
  const cb = contraste(m.corPrincipal, '#ffffff')
  const logo = m.logoClaro || logoDaMarca(m)
  return (
    <div className="marca-previa" style={{ fontFamily: FONTES[m.fonte] }}>
      <div className="mp-lateral">
        <div className="mp-logo">{logo ? <img src={logo} alt="" /> : <b>{m.nome || 'Marca'}</b>}</div>
        <span className="mp-item ativo" style={{ background: `color-mix(in srgb, ${m.corPrincipal} 12%, #ffffff)`, color: m.corPrincipal }}>Dashboards</span>
        <span className="mp-item">Chamados</span>
        <span className="mp-item">Comptrade</span>
      </div>
      <div className="mp-corpo">
        <b className="mp-titulo">Atendimento</b>
        <div className="mp-barras">
          {[72, 54, 88, 63].map((v, i) => <span key={i} style={{ height: `${v}%`, background: i % 2 ? m.corApoio : m.corPrincipal }} />)}
        </div>
        <span className="mp-botao" style={{ background: m.corPrincipal, color: sobre }}>Salvar alterações</span>
      </div>
      <p className="mp-contraste">
        Texto do botão sobre a cor principal: <b className={ct >= 4.5 ? 'ok' : 'ruim'}>{ct.toFixed(1).replace('.', ',')}:1 {ct >= 4.5 ? '✓' : '✗'}</b>
        {sobre !== '#ffffff' && <span> · com texto branco seria {cb.toFixed(1).replace('.', ',')}:1, por isso o portal usa texto escuro nesta cor.</span>}
      </p>
    </div>
  )
}

export default function Marcas() {
  const { notify } = useNotification()
  const [marcas, setMarcas] = useState<Marca[]>([])
  const [ambientes, setAmbientes] = useState<Ambiente[]>([])
  const [selecionada, setSelecionada] = useState<number | null>(null)
  const [rascunho, setRascunho] = useState<Marca>(NOVA)
  const [salvando, setSalvando] = useState(false)

  const carregar = useCallback(async (manter?: number) => {
    const [rm, ra] = await Promise.all([trackPromise(services.api.novoPortalMarcas()), services.api.environmentsRelatedAll()])
    if (rm.fail) { notify(rm.error || 'Não foi possível carregar as marcas.', { variant: 'error' }); return }
    const lista: Marca[] = rm.data || []
    const ambs: Ambiente[] = ra.fail ? [] : (ra.data || []).map((a: any) => ({ id: a.id, name: a.name, brandId: a.brandId }))
    setMarcas(lista)
    setAmbientes(ambs)
    atualizarMarcas(lista, ambs.map(a => ({ id: a.id, name: a.name, brandId: lista.find(m => m.ambientes.includes(a.id))?.id ?? null })))
    const alvo = lista.find(m => m.id === manter) || lista[0]
    if (alvo) { setSelecionada(alvo.id); setRascunho({ ...alvo }) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { carregar() }, [carregar])

  const original = marcas.find(m => m.id === selecionada)
  const mudou = !original || JSON.stringify(original) !== JSON.stringify(rascunho)
  const valido = rascunho.nome.trim().length > 0 && HEX.test(rascunho.corPrincipal) && HEX.test(rascunho.corApoio)
  const outraMarcaDe = useMemo(() => {
    const mapa: Record<number, string> = {}
    marcas.forEach(m => { if (m.id !== rascunho.id) m.ambientes.forEach(a => { mapa[a] = m.nome }) })
    return mapa
  }, [marcas, rascunho.id])

  const mudar = (parcial: Partial<Marca>) => setRascunho(r => ({ ...r, ...parcial }))

  const salvar = async () => {
    setSalvando(true)
    const res = await trackPromise(services.api.novoPortalSalvarMarca({
      id: rascunho.id || undefined, nome: rascunho.nome.trim(), corPrincipal: rascunho.corPrincipal, corApoio: rascunho.corApoio,
      fonte: rascunho.fonte, logoClaro: rascunho.logoClaro || null, logoEscuro: rascunho.logoEscuro || null
    }))
    if (res.fail) { setSalvando(false); notify(res.error || 'Não foi possível salvar a marca.', { variant: 'error' }); return }
    const id = res.data.id
    const vinc = await trackPromise(services.api.novoPortalVincularAmbientes(id, rascunho.ambientes))
    setSalvando(false)
    if (vinc.fail) notify(vinc.error || 'A marca foi salva, mas os ambientes não.', { variant: 'warning' })
    else notify('Marca salva. O portal já usa as cores novas nos ambientes dela.', { variant: 'success' })
    await carregar(id)
  }

  const excluir = async () => {
    const res = await trackPromise(services.api.novoPortalExcluirMarca(rascunho.id))
    if (res.fail) { notify(res.error || 'Não foi possível excluir.', { variant: 'error' }); return }
    notify(`Marca excluída. Os ambientes dela voltaram para a Trade Talentos.`, { variant: 'success' })
    await carregar()
  }

  return (
    <div className="wrapper-container-content bgwhite">
      <div className="wrapper-page-content" id="wrapper-page-content">
        <PageTitle subText="Cada ambiente é atendido por uma empresa do grupo e o portal veste a marca dela">
          <div>Marcas</div>
        </PageTitle>

        <div className="marcas-grade">
          <div className="marcas-lista panel">
            <div className="panel-body">
              {marcas.map(m => (
                <button key={m.id} type="button" className="marcas-item" aria-current={m.id === selecionada}
                        onClick={() => { setSelecionada(m.id); setRascunho({ ...m }) }}>
                  <span className="marcas-cores"><i style={{ background: m.corPrincipal }} /><i style={{ background: m.corApoio }} /></span>
                  <span><b>{m.nome}</b><small>{m.ambientes.length === 1 ? '1 ambiente' : `${m.ambientes.length} ambientes`}</small></span>
                </button>
              ))}
              <Button startIcon={<AddIcon />} onClick={() => { setSelecionada(null); setRascunho({ ...NOVA }) }} sx={{ mt: 1 }}>Nova marca</Button>
            </div>
          </div>

          <div className="panel">
            <div className="panel-body marcas-editor">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField size="small" label="Nome" value={rascunho.nome} onChange={e => mudar({ nome: e.target.value })} inputProps={{ maxLength: 60 }} fullWidth required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField size="small" select label="Fonte" value={rascunho.fonte} onChange={e => mudar({ fonte: e.target.value })} fullWidth>
                    {Object.keys(FONTES).map(f => <MenuItem key={f} value={f} style={{ fontFamily: FONTES[f] }}>{f}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <CampoCor rotulo="Cor principal" valor={rascunho.corPrincipal} aoMudar={v => mudar({ corPrincipal: v })} dica="Botões, item ativo do menu, destaques" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CampoCor rotulo="Cor de apoio" valor={rascunho.corApoio} aoMudar={v => mudar({ corApoio: v })} dica="Segunda série dos gráficos e detalhes" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CampoLogo rotulo="Logo para fundo claro" valor={rascunho.logoClaro} fundo="#ffffff" aoMudar={v => mudar({ logoClaro: v })} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CampoLogo rotulo="Logo para fundo escuro" valor={rascunho.logoEscuro} fundo="#1c1f26" aoMudar={v => mudar({ logoEscuro: v })} />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={ambientes}
                    value={ambientes.filter(a => rascunho.ambientes.includes(a.id))}
                    getOptionLabel={a => a.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, lista) => mudar({ ambientes: lista.map(a => a.id) })}
                    renderOption={(props, a) => (
                      <li {...props} key={a.id}>
                        <span style={{ flex: 1 }}>{a.name}</span>
                        {outraMarcaDe[a.id] && <small style={{ color: '#8b92a1' }}>hoje: {outraMarcaDe[a.id]}</small>}
                      </li>
                    )}
                    renderTags={(lista, getTagProps) => lista.map((a, i) => <Chip size="small" label={a.name} {...getTagProps({ index: i })} key={a.id} />)}
                    renderInput={p => <TextField {...p} label="Ambientes desta marca" helperText="Cada ambiente tem uma marca só: escolher aqui tira da outra. Ambiente sem marca fica com a Trade Talentos." />}
                  />
                </Grid>
              </Grid>

              <h3 className="marcas-sub">Prévia</h3>
              <Previa m={rascunho} />

              <div className="marcas-acoes">
                {rascunho.id > 0 && (
                  <Popconfirm title={`Excluir ${rascunho.nome}? Os ambientes dela voltam para a Trade Talentos.`} okText="Excluir" cancelText="Cancelar" onConfirm={excluir}>
                    <Button color="error" startIcon={<DeleteIcon />}>Excluir</Button>
                  </Popconfirm>
                )}
                <span style={{ flex: 1 }} />
                <Button variant="contained" startIcon={<SaveIcon />} disabled={!valido || !mudou || salvando} onClick={salvar}>
                  {rascunho.id ? 'Salvar marca' : 'Criar marca'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
